// Shared trawl utilities — used by useTrawl and useSnowball hooks.

import {
  fetchCitations,
  fetchCocited,
  fetchRecommendations,
  fetchTopCiters,
  resolvePaperId,
  S2AuthError,
  type S2CitationEdge,
} from "@/services/s2-client";
import { deduplicatePapers, edgeToPaperCreate, s2PaperToPaperCreate } from "@/services/dedup";
import { stratifyPapers } from "@/lib/stratify-papers";
import { mergeLinks, tagLinks, type ProvenanceParent } from "@/lib/provenance";
import type { RawTrawledPaper } from "@/services/dedup";
import apiClient from "@/lib/api-client";
import type { DiscoveryRelation, PaperWithRole, PaperBatchResult } from "@/types/paper";
import type { DirectionLimits, DirectionWeights } from "@/lib/setup/recency";
import type { SeedNetwork } from "@/lib/setup/preview";
import type { Recency } from "@/types/concept";

export const BATCH_SIZE = 100;
export const MAX_CONCURRENT_BATCHES = 3;

/**
 * Resolve a paper's S2 ID from its paper_id, DOI, or title.
 */
export async function resolveS2Id(
  paper: Pick<PaperWithRole, "paper_id" | "doi" | "title">,
  signal: AbortSignal,
  options?: { strict?: boolean },
): Promise<string | null> {
  if (paper.paper_id) return paper.paper_id;
  if (paper.doi) return `DOI:${paper.doi}`;
  return resolvePaperId(paper.title, signal, options);
}

/**
 * POST papers to server in batches with concurrency limit.
 * `onBatch` fires once per HTTP batch as its request resolves (live progress);
 * a failed sibling still rejects the whole call, after its peers were reported.
 */
export async function postBatches(
  conceptId: string,
  papers: RawTrawledPaper[],
  signal: AbortSignal,
  addedSource: string = "trawl",
  onBatch?: (batch: RawTrawledPaper[], result: PaperBatchResult) => void,
): Promise<PaperBatchResult> {
  const batches: RawTrawledPaper[][] = [];
  for (let i = 0; i < papers.length; i += BATCH_SIZE) {
    batches.push(papers.slice(i, i + BATCH_SIZE));
  }

  const result: PaperBatchResult = { added: 0, duplicates: 0, merged: 0 };

  for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
    if (signal.aborted) break;
    const chunk = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
    const results = await Promise.all(
      chunk.map((batch) =>
        apiClient
          .post(`api/concepts/${conceptId}/papers`, {
            json: { papers: batch, role: "trawled", added_source: addedSource },
            signal,
            timeout: 120_000,
          })
          .json<PaperBatchResult>()
          .then((r) => { onBatch?.(batch, r); return r; }),
      ),
    );
    for (const r of results) {
      result.added += r.added;
      result.duplicates += r.duplicates;
      result.merged += r.merged;
    }
  }

  return result;
}

/**
 * Fetch citations and/or references for a paper, converting each edge
 * to a RawTrawledPaper. Returns deduplicated results.
 */
export async function fetchPaperNetwork(
  s2Id: string,
  options: {
    maxResults: number;
    includeReferences: boolean;
    includeRecommendations: boolean;
    signal: AbortSignal;
    /** Called as individual S2 responses arrive with running total of results for this paper. */
    onProgress?: (resultsSoFar: number) => void;
    /**
     * When set, every returned paper carries a discovered_via link to this
     * parent. Omit for untagged results (behavior unchanged).
     */
    parent?: ProvenanceParent;
    /** Per-direction multipliers (concept recency, spec §3). Omit = today's limits. */
    directionWeights?: DirectionWeights;
    /** Concept recency: classic/balanced bring a seed's most-cited citers. Omit = S2's list. */
    recency?: Recency;
  },
): Promise<RawTrawledPaper[]> {
  const { maxResults, includeReferences, includeRecommendations, signal, onProgress, parent } = options;
  const overfetchLimit = maxResults * 3;
  const w = options.directionWeights ?? { references: 1, citations: 1, recommendations: 1, cocited: 0 };
  const citationsLimit = Math.round(overfetchLimit * w.citations);
  const referencesLimit = Math.round(overfetchLimit * w.references);
  const recommendationsLimit = Math.round(20 * w.recommendations);
  const cocitedLimit = Math.round(Math.min(overfetchLimit, 100) * w.cocited);
  let totalResults = 0;

  const notify = (delta: number) => {
    totalResults += delta;
    onProgress?.(totalResults);
  };

  // Fire all network fetches concurrently — they share the rate limiter
  // so throughput is still gated, but we overlap network latency.
  const fetches: Promise<RawTrawledPaper[]>[] = [];

  // S2 "citations" = papers that CITE the parent; "references" = papers the
  // parent cites; recommendations = similar papers seeded from the parent.
  // Citations (always)
  if (citationsLimit > 0) fetches.push(
    fetchCiters(s2Id, options.recency, {
      limit: citationsLimit, signal,
      onPage: (n) => notify(n - (totalResults)),
    })
      .then((edges) => {
        const papers = edges.map((e) => edgeToPaperCreate(e, "citations")).filter(Boolean) as RawTrawledPaper[];
        return parent ? tagLinks(papers, parent, "cites_parent") : papers;
      })
      .catch((err) => { if (signal.aborted || err instanceof S2AuthError) throw err; return []; }),
  );

  // References (optional)
  if (includeReferences && referencesLimit > 0) {
    fetches.push(
      fetchCitations(s2Id, {
        direction: "references", limit: referencesLimit, signal,
        onPage: (n) => notify(n - (totalResults)),
      })
        .then((edges) => {
          const papers = edges.map((e) => edgeToPaperCreate(e, "references")).filter(Boolean) as RawTrawledPaper[];
          return parent ? tagLinks(papers, parent, "cited_by_parent") : papers;
        })
        .catch((err) => { if (signal.aborted || err instanceof S2AuthError) throw err; return []; }),
    );
  }

  // Co-cited: stands in for the references of seeds that have none (the
  // backend returns [] for a seed with references), so it follows them.
  if (includeReferences && cocitedLimit > 0) {
    fetches.push(
      fetchCocited(s2Id, cocitedLimit, signal)
        .then((edges) => {
          const papers = edges.map((e) => edgeToPaperCreate(e, "references")).filter(Boolean) as RawTrawledPaper[];
          notify(papers.length);
          return parent ? tagLinks(papers, parent, "cocited_with") : papers;
        }),
    );
  }

  // Recommendations (optional)
  if (includeRecommendations && recommendationsLimit > 0) {
    fetches.push(
      fetchRecommendations(s2Id, { limit: recommendationsLimit, signal })
        .then((recs) => {
          const papers = recs.map((r) => s2PaperToPaperCreate(r)).filter(Boolean) as RawTrawledPaper[];
          notify(papers.length);
          return parent ? tagLinks(papers, parent, "recommended_with") : papers;
        })
        .catch((err) => { if (signal.aborted || err instanceof S2AuthError) throw err; return []; }),
    );
  }

  const results = await Promise.all(fetches);
  const all = results.flat();
  const selected = stratifyPapers(all, maxResults);
  if (!parent) return selected;

  // Stratification keeps one row per paper; a paper in several lists (e.g. it
  // cites the parent AND is recommended with it) must keep every relation.
  const linksById = new Map<string, NonNullable<RawTrawledPaper["discovered_via"]>>();
  for (const p of all) {
    if (!p.paper_id || !p.discovered_via) continue;
    linksById.set(p.paper_id, mergeLinks(linksById.get(p.paper_id) ?? [], p.discovered_via));
  }
  return selected.map((p) =>
    p.paper_id && linksById.has(p.paper_id)
      ? { ...p, discovered_via: linksById.get(p.paper_id) }
      : p,
  );
}

const RELATION: Record<keyof SeedNetwork, DiscoveryRelation> = {
  citations: "cites_parent",
  references: "cited_by_parent",
  recommendations: "recommended_with",
  cocited: "cocited_with",
};

/** Setup preview: one seed's network split by direction, untagged (cacheable per seed).
 *  All or nothing: throws S2UnavailableError if any direction couldn't be
 *  fetched, so a rate-limited direction is never cached as "no papers". */
export async function fetchSeedNetwork(
  s2Id: string, limits: DirectionLimits, signal: AbortSignal, recency: Recency = "balanced",
  doi: string | null = null,
): Promise<SeedNetwork> {
  const edges = (direction: "citations" | "references", limit: number) =>
    limit > 0
      ? (direction === "citations"
          ? fetchCiters(s2Id, recency, { limit, signal, strict: true, doi })
          : fetchCitations(s2Id, { direction, limit, signal, strict: true }))
          .then((es) => es.map((e) => edgeToPaperCreate(e, direction)).filter(Boolean) as RawTrawledPaper[])
      : Promise.resolve([] as RawTrawledPaper[]);
  const [citations, references, recommendations, cocited] = await Promise.all([
    edges("citations", limits.citations),
    edges("references", limits.references),
    limits.recommendations > 0
      ? fetchRecommendations(s2Id, { limit: limits.recommendations, signal, strict: true })
          .then((rs) => rs.map((r) => s2PaperToPaperCreate(r)).filter(Boolean) as RawTrawledPaper[])
      : Promise.resolve([] as RawTrawledPaper[]),
    // Never throws (except abort): [] is "no co-cited direction", not an outage.
    limits.cocited > 0
      ? fetchCocited(s2Id, limits.cocited, signal)
          .then((es) => es.map((e) => edgeToPaperCreate(e, "references")).filter(Boolean) as RawTrawledPaper[])
      : Promise.resolve([] as RawTrawledPaper[]),
  ]);
  return { citations, references, recommendations, cocited };
}

/** How recency picks a seed's citers. S2's /citations is newest-first only, so
 *  classic takes the most-cited (OpenAlex) and balanced takes half of each. */
export function citerMix(recency: Recency | undefined): "most_cited" | "half" | null {
  if (recency === "classic") return "most_cited";
  if (recency === "balanced") return "half";
  return null;
}

/** A seed's citers for a recency. Most-cited come first; S2's newest fill the
 *  rest, deduped. When the top-citers endpoint has nothing (ring off, OpenAlex
 *  down, no DOI) this is exactly S2's list, as before. S2 failures keep their
 *  meaning (`strict` throws), so setup networks stay all-or-nothing. */
export async function fetchCiters(
  s2Id: string,
  recency: Recency | undefined,
  options: {
    limit: number; signal: AbortSignal; strict?: boolean; onPage?: (edgesSoFar: number) => void;
    /** The seed's DOI, for the top-citers call (see fetchTopCiters). */
    doi?: string | null;
  },
): Promise<S2CitationEdge[]> {
  const { limit, signal, strict, onPage, doi } = options;
  const newest = () => fetchCitations(s2Id, { direction: "citations", limit, signal, strict, onPage });
  const mix = citerMix(recency);
  if (!mix) return newest();

  let top: S2CitationEdge[];
  let rest: S2CitationEdge[];
  if (mix === "most_cited") {
    top = await fetchTopCiters(s2Id, limit, signal, doi);
    // Few resolved citers: fill from S2 (which then holds every citer anyway).
    rest = top.length < limit ? await newest() : [];
  } else {
    [top, rest] = await Promise.all([fetchTopCiters(s2Id, Math.ceil(limit / 2), signal, doi), newest()]);
  }
  if (top.length) onPage?.(top.length);
  const seen = new Set<string>();
  const out: S2CitationEdge[] = [];
  for (const e of [...top, ...rest]) {
    const id = e.citingPaper?.paperId;
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    out.push(e); // no paperId: kept as fetchCitations returns it (dedup handles it later)
    if (out.length >= limit) break;
  }
  return out;
}

/** Tag each seed's network with its parent and merge papers found more than once. */
export function networkToPapers(
  items: { network: SeedNetwork; parent: ProvenanceParent }[],
): RawTrawledPaper[] {
  const byKey = new Map<string, RawTrawledPaper>();
  for (const { network, parent } of items) {
    for (const dir of Object.keys(RELATION) as (keyof SeedNetwork)[]) {
      for (const p of tagLinks(network[dir] ?? [], parent, RELATION[dir])) {
        const key = p.paper_id ?? p.doi ?? p.title.toLowerCase();
        const prev = byKey.get(key);
        byKey.set(key, prev
          ? { ...prev, discovered_via: mergeLinks(prev.discovered_via ?? [], p.discovered_via ?? []) }
          : p);
      }
    }
  }
  return [...byKey.values()];
}

export { deduplicatePapers, type RawTrawledPaper };
